/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Feature Flags & Maintenance Mode View (Task 11.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  Sliders,
  Zap,
  Search,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Layers,
  Cpu,
  UserCheck,
  Video,
  Mic,
  CreditCard,
  Sparkles,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import type { FeatureFlagItem } from '@sofia/shared-types';

interface FeatureFlagsViewProps {
  embedded?: boolean;
}

export const FeatureFlagsView: React.FC<FeatureFlagsViewProps> = ({ embedded = false }) => {
  const { t } = useI18n();
  const domains = getAdminDomains();

  const [flags, setFlags] = useState<FeatureFlagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Critical Flag Confirmation Modal State
  const [pendingCriticalFlag, setPendingCriticalFlag] = useState<{
    key: string;
    name: string;
    targetState: boolean;
  } | null>(null);

  const fetchFlags = async () => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/feature-flags`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const body = ((await res.json())) as any;
      if (body.success && Array.isArray(body.data)) {
        setFlags(body.data);
      }
    } catch (err: any) {
      console.error('[FeatureFlagsView] fetch error:', err);
      setToastMsg({ type: 'error', text: err.message || 'Failed to load feature flags' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const executeToggle = async (key: string, isEnabled: boolean) => {
    try {
      setTogglingKey(key);
      const targetFlag = flags.find((f) => f.key === key);
      const flagName = targetFlag?.name || key;

      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/feature-flags`, {
        method: 'PATCH',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, isEnabled }),
      });

      if (!res.ok) {
        const errJson = ((await res.json().catch(() => ({})))) as any;
        throw new Error(errJson.error?.message || `HTTP ${res.status}`);
      }

      const body = ((await res.json())) as any;
      if (body.success && body.data) {
        setFlags((prev) => prev.map((f) => (f.key === key ? body.data : f)));
        const stateText = isEnabled ? t('admin.featureFlags.status.enabled') : t('admin.featureFlags.status.disabled');
        setToastMsg({
          type: 'success',
          text: t('admin.featureFlags.toast.toggleSuccess', { name: flagName, state: stateText }),
        });
        setTimeout(() => setToastMsg(null), 5000);
      }
    } catch (err: any) {
      console.error('[FeatureFlagsView] toggle error:', err);
      setToastMsg({
        type: 'error',
        text: t('admin.featureFlags.toast.toggleError', { error: err.message }),
      });
    } finally {
      setTogglingKey(null);
    }
  };

  const handleToggleClick = (flag: FeatureFlagItem) => {
    const nextState = !flag.isEnabled;

    // If toggling ON a system critical flag (like maintenance_mode), require confirmation
    if (flag.isSystemCritical && nextState) {
      setPendingCriticalFlag({
        key: flag.key,
        name: flag.name,
        targetState: nextState,
      });
      return;
    }

    executeToggle(flag.key, nextState);
  };

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'system':
        return <Layers className="w-3.5 h-3.5 text-blue-500" />;
      case 'identity':
        return <UserCheck className="w-3.5 h-3.5 text-indigo-500" />;
      case 'ai':
        return <Cpu className="w-3.5 h-3.5 text-purple-500" />;
      case 'media':
        return <Video className="w-3.5 h-3.5 text-pink-500" />;
      case 'voice':
        return <Mic className="w-3.5 h-3.5 text-amber-500" />;
      case 'billing':
        return <CreditCard className="w-3.5 h-3.5 text-emerald-500" />;
      case 'growth':
        return <Sparkles className="w-3.5 h-3.5 text-cyan-500" />;
      default:
        return <Sliders className="w-3.5 h-3.5 text-muted-fg" />;
    }
  };

  const filteredFlags = flags.filter((f) => {
    const matchesCategory = selectedCategory === 'all' || f.category === selectedCategory;
    const matchesSearch =
      searchQuery.trim() === '' ||
      f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className={`space-y-6 ${embedded ? '' : 'pb-24'}`}>
      {/* Header (Only if not embedded in an existing page) */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2.5">
              <Sliders className="w-6 h-6 text-primary" />
              {t('admin.featureFlags.title')}
            </h1>
            <p className="text-xs text-muted-fg mt-1 max-w-2xl">
              {t('admin.featureFlags.subtitle')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shadow-sm">
              <Zap className="w-3.5 h-3.5 shrink-0" />
              {t('admin.featureFlags.kvSyncStatus')}
            </span>
            <button
              type="button"
              onClick={fetchFlags}
              disabled={loading}
              className="p-2 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-xl hover:bg-surface-elevated transition-colors disabled:opacity-50"
              title="Refresh flags"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-muted-fg ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {/* Toast Alert */}
      {toastMsg && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center gap-2.5 shadow-sm ${
            toastMsg.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400'
              : 'bg-destructive/10 border border-destructive/20 text-destructive'
          }`}
        >
          {toastMsg.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          <span>{toastMsg.text}</span>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-2xl bg-surface border border-surface-border">
        {/* Categories Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 text-xs">
          {[
            { id: 'all', label: t('admin.featureFlags.categories.all') },
            { id: 'system', label: t('admin.featureFlags.categories.system') },
            { id: 'identity', label: t('admin.featureFlags.categories.identity') },
            { id: 'ai', label: t('admin.featureFlags.categories.ai') },
            { id: 'media', label: t('admin.featureFlags.categories.media') },
            { id: 'voice', label: t('admin.featureFlags.categories.voice') },
            { id: 'billing', label: t('admin.featureFlags.categories.billing') },
            { id: 'growth', label: t('admin.featureFlags.categories.growth') },
          ].map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-xl whitespace-nowrap transition-colors font-medium ${
                selectedCategory === cat.id
                  ? 'bg-primary text-primary-fg shadow-sm'
                  : 'bg-surface-elevated text-muted-fg hover:text-foreground hover:bg-surface-border/50'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.featureFlags.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/70 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Flags Grid */}
      {loading ? (
        <div className="p-12 text-center text-muted-fg animate-pulse space-y-3">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-sm">{t('common.loading')}</p>
        </div>
      ) : filteredFlags.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-surface-border rounded-2xl bg-surface text-muted-fg text-xs">
          {t('table.noRecords')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredFlags.map((flag) => {
            const isProcessing = togglingKey === flag.key;
            return (
              <div
                key={flag.key}
                className={`p-5 rounded-2xl border transition-all flex flex-col justify-between gap-4 ${
                  flag.isEnabled
                    ? 'bg-surface border-surface-border hover:border-primary/40 shadow-sm'
                    : 'bg-surface/60 border-surface-border/70 opacity-80'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-surface-elevated border border-surface-border">
                        {getCategoryIcon(flag.category)}
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                          {flag.name}
                          {flag.isSystemCritical && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                              {t('admin.featureFlags.status.critical')}
                            </span>
                          )}
                        </h3>
                        <span className="font-mono text-[10px] text-muted-fg">{flag.key}</span>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <div className="shrink-0 flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={flag.isEnabled}
                          disabled={isProcessing}
                          onChange={() => handleToggleClick(flag)}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 bg-surface-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            flag.isSystemCritical && flag.isEnabled
                              ? 'peer-checked:bg-amber-500'
                              : 'peer-checked:bg-primary'
                          } ${isProcessing ? 'opacity-50' : ''}`}
                        ></div>
                      </label>
                    </div>
                  </div>

                  <p className="text-[11px] text-muted-fg leading-relaxed">
                    {flag.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-surface-border/50 flex items-center justify-between text-[10px] text-muted-fg">
                  <span className="capitalize">{flag.category}</span>
                  <span className="font-mono">
                    {new Date(flag.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Critical Maintenance Warning Modal */}
      {pendingCriticalFlag && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-amber-500">
              <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {t('admin.featureFlags.maintenanceWarningModal.title')}
                </h3>
                <span className="text-xs text-muted-fg font-mono">{pendingCriticalFlag.name}</span>
              </div>
            </div>

            <p className="text-xs text-muted-fg leading-relaxed">
              {t('admin.featureFlags.maintenanceWarningModal.message')}
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPendingCriticalFlag(null)}
                className="px-4 py-2 text-xs font-medium text-foreground bg-surface-elevated hover:bg-surface-border rounded-xl transition-colors"
              >
                {t('admin.featureFlags.maintenanceWarningModal.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const { key, targetState } = pendingCriticalFlag;
                  setPendingCriticalFlag(null);
                  executeToggle(key, targetState);
                }}
                className="px-4 py-2 text-xs font-semibold text-amber-950 bg-amber-500 hover:bg-amber-400 rounded-xl shadow-sm transition-colors"
              >
                {t('admin.featureFlags.maintenanceWarningModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const FeatureFlagsPage = FeatureFlagsView;
